import { defineComponent as _defineComponent } from 'vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { EnButton } from 'Components/base/EnButton';
import { Columns3 as ColumnsIcon, Eye as EyeIcon, GripVertical, Undo2 as RestoreIcon } from 'lucide-vue-next';
import EnCheckboxButton from 'Components/inputs/EnCheckbox/EnCheckbox.vue';
import EnDraggableWrapper from 'Components/base/EnDraggableWrapper/EnDraggableWrapper.vue';
import EnTooltip from 'Components/base/EnTooltip/EnTooltip.vue';
import { EnMenu, MenuSeparator } from 'Components/base/EnMenu';
import { EnItem } from 'Components/base/EnItem';
import { buildStaticId } from 'Components/utils/buildStaticId';
import { useColumnVisibilityMenu } from '../composables/useColumnVisibilityMenu';
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnColumnVisibilityMenu',
    props: {
        /**
         * All columns of the data grid, in the order they are displayed in.
         * Only the ones the user may hide are listed in the menu.
         */
        columns: (Array),
        /**
         * Shows all columns, including columns which are currently hidden.
         */
        showAllColumns: Function,
        /**
         * Puts the data grid back into the view its module defines.
         */
        restoreDefaultView: Function,
        /**
         * If false, the view is the default view and there is nothing to restore. The button is hidden.
         */
        showRestoreDefaultViewButton: Boolean,
        /**
         * Displays the columns in a new order. Takes the data fields in the order they should appear in.
         */
        reorderColumns: Function,
        /**
         * Hides the given column, or shows it again if it is already hidden.
         */
        toggleVisibilityColumn: Function,
        /**
         * Static Id for testing purpose
         */
        staticId: {
            type: String,
        },
    },
    emits: ['column-toggled', 'click', 'columns-show-all'],
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const { t } = useI18n();
        const props = __props;
        const emit = __emit;
        const button = ref();
        const menuComponent = ref();
        const { draggableColumns, clippedTitles, isLastVisibleColumn, onColumnDragged, checkTitleIsClipped } = useColumnVisibilityMenu(() => props.columns, orderedDataFields => props.reorderColumns?.(orderedDataFields));
        /**
         * Hides the column, or shows it again. The last column on display stays as it is,  datagrid
         * without columns has nothing left to show.
         */
        function toggleColumn(column) {
            if (isLastVisibleColumn(column))
                return;
            props.toggleVisibilityColumn?.(column);
        }
        /**
         * The menu stays open while columns are toggled, so several can be changed in one go. The buttons
         * above the list finish the whole list at once and close it.
         */
        function showAllColumnsAndClose() {
            menuComponent.value?.setOpen(false);
            props.showAllColumns?.();
        }
        function restoreDefaultViewAndClose() {
            menuComponent.value?.setOpen(false);
            props.restoreDefaultView?.();
        }
        /**
         * Builds a static ID for each column based on its value.
         * @param column
         */
        function buildId(column) {
            return buildStaticId('column', column.value, true);
        }
        /**
         * Handles the click event on the column visibility button.
         * Emits a 'click' event to notify parent components.
         * @param {Event} event - The click event.
         */
        function handleClick(event) {
            emit('click', event);
        }
        function buildIdForRestoreDefaultView() {
            const id = 'columns-restore-default-view';
            if (props.staticId) {
                return buildStaticId(id, props.staticId, true);
            }
            return id;
        }
        function buildIdForVisibilityMenu() {
            const id = 'columns-visibility-menu';
            if (props.staticId) {
                return buildStaticId(id, props.staticId, true);
            }
            return id;
        }
        const getButtonId = computed(() => {
            return button?.value?.buildId?.();
        });
        const __returned__ = { t, props, emit, button, menuComponent, draggableColumns, clippedTitles, isLastVisibleColumn, onColumnDragged, checkTitleIsClipped, toggleColumn, showAllColumnsAndClose, restoreDefaultViewAndClose, buildId, handleClick, buildIdForRestoreDefaultView, buildIdForVisibilityMenu, getButtonId, get EnButton() { return EnButton; }, get ColumnsIcon() { return ColumnsIcon; }, get EyeIcon() { return EyeIcon; }, get GripVertical() { return GripVertical; }, get RestoreIcon() { return RestoreIcon; }, EnCheckboxButton, EnDraggableWrapper, EnTooltip, get EnMenu() { return EnMenu; }, get MenuSeparator() { return MenuSeparator; }, get EnItem() { return EnItem; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
